// src/components/ICT/Sidebar.js
import React, { useState } from 'react';
import {
  FiHome,
  FiFileText,
  FiCreditCard,
  FiBarChart2,
  FiSettings,
  FiHelpCircle,
  FiChevronDown,
  FiChevronRight,
  FiCoffee,
  FiShoppingBag,
  FiScissors,
  FiBox,
  FiStar,
  FiGlobe,
  FiMusic,
  FiPackage,
  FiClipboard,
  FiTrendingUp,
  FiAlertCircle,
  FiServer,
  FiShield,
  FiDatabase,
  FiCloud,
  FiMonitor,
  FiWifi,
  FiHardDrive,
  FiUsers,
  FiLock,
  FiCpu,
  FiCode,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/elegantelogo.png';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [openDropdowns, setOpenDropdowns] = useState({
    infrastructure: false,
    security: false,
    systems: false,
    network: false,
    support: false,
  });

  const toggleDropdown = (key) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Main ICT Menu Items
  const menuItems = [
    { 
      name: 'dashboard', 
      label: 'Dashboard',
      icon: <FiHome />, 
      path: '/Dashboard/ICT' 
    },
    { 
      name: 'users', 
      label: 'System Users',
      icon: <FiMonitor />, 
      path: '/system/users' 
    },
  ];

  // Infrastructure Management
  const infrastructureItems = [
    { 
      name: 'servers', 
      label: 'Server Management',
      icon: <FiServer />, 
      path: '/ict/infrastructure/servers' 
    },
    { 
      name: 'storage', 
      label: 'Storage & Backup',
      icon: <FiHardDrive />, 
      path: '/ict/infrastructure/storage' 
    },
    { 
      name: 'cloud', 
      label: 'Cloud Services',
      icon: <FiCloud />, 
      path: '/ict/infrastructure/cloud' 
    },
  ];

  // Security Management
  const securityItems = [
    { 
      name: 'access-control', 
      label: 'Access Control',
      icon: <FiLock />, 
      path: '/ict/security/access' 
    },
    { 
      name: 'cyber-security', 
      label: 'Cyber Security',
      icon: <FiShield />, 
      path: '/ict/security/cyber' 
    },
    { 
      name: 'audit-logs', 
      label: 'Audit Logs',
      icon: <FiFileText />, 
      path: '/ict/security/audit' 
    },
  ];

  // Systems Management
  const systemsItems = [
    { 
      name: 'applications', 
      label: 'Applications',
      icon: <FiCode />, 
      path: '/ict/systems/applications' 
    },
    { 
      name: 'databases', 
      label: 'Databases',
      icon: <FiDatabase />, 
      path: '/ict/systems/databases' 
    },
    { 
      name: 'updates', 
      label: 'System Updates',
      icon: <FiCpu />, 
      path: '/ict/systems/updates' 
    },
  ];

  // Network Management
  const networkItems = [
    { 
      name: 'network-monitoring', 
      label: 'Network Monitoring',
      icon: <FiWifi />, 
      path: '/ict/network/monitoring' 
    },
    { 
      name: 'network-security', 
      label: 'Network Security',
      icon: <FiShield />, 
      path: '/ict/network/security' 
    },
    { 
      name: 'bandwidth', 
      label: 'Bandwidth Management',
      icon: <FiTrendingUp />, 
      path: '/ict/network/bandwidth' 
    },
  ];

  // ICT Support Items
  const supportItems = [
    { 
      name: 'helpdesk', 
      label: 'IT Helpdesk',
      icon: <FiUsers />, 
      path: '/ict/support/helpdesk' 
    },
    { 
      name: 'tickets', 
      label: 'Support Tickets',
      icon: <FiClipboard />, 
      path: '/ict/support/tickets' 
    },
    { 
      name: 'knowledge-base', 
      label: 'Knowledge Base',
      icon: <FiFileText />, 
      path: '/ict/support/knowledge' 
    },
  ];

  // Reports & Analytics
  const reportItems = [
    { 
      name: 'system-reports', 
      label: 'System Reports',
      icon: <FiBarChart2 />, 
      path: '/ict/reports/system' 
    },
    { 
      name: 'performance', 
      label: 'Performance Analytics',
      icon: <FiTrendingUp />, 
      path: '/ict/reports/performance' 
    },
  ];

  const otherItems = [
    { 
      name: 'settings', 
      label: 'Settings',
      icon: <FiSettings />, 
      path: '/ict/settings' 
    },
  ];

  const getActiveMenu = () => {
    const currentPath = location.pathname;
    
    const allItems = [
      ...menuItems, 
      ...infrastructureItems, 
      ...securityItems, 
      ...systemsItems, 
      ...networkItems,
      ...supportItems,
      ...reportItems,
      ...otherItems
    ];
    const activeItem = allItems.find(item => currentPath === item.path);
    return activeItem ? activeItem.name : null;
  };

  const activeMenuItem = getActiveMenu();

  const handleMenuClick = (item) => {
    navigate(item.path);
  };

  const isDropdownActive = (items) => {
    return items.some(item => activeMenuItem === item.name);
  };

  return (
    <aside className="ict-sidebar">
      <div className="ict-brand">
        <div className="brand-logo-full">
          <img src={logo} alt="Elegante Restaurant Logo" />
        </div>
      </div>

      <div className="menu-list">
        <div className="menu-section-label">📡 ICT Dashboard</div> 

        {menuItems.map((item) => (
          <button
            key={item.name}
            className={`menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
            onClick={() => handleMenuClick(item)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-text">{item.label}</span>
          </button>
        ))}

        {/* Infrastructure Dropdown */}
        <div className="menu-dropdown">
          <button
            className={`menu-btn dropdown-btn ${isDropdownActive(infrastructureItems) ? 'active' : ''}`}
            onClick={() => toggleDropdown('infrastructure')}
          >
            <span className="menu-icon"><FiServer /></span>
            <span className="menu-text">Infrastructure</span>
            <span className="dropdown-arrow">
              {openDropdowns.infrastructure ? <FiChevronDown /> : <FiChevronRight />}
            </span>
          </button>
          {openDropdowns.infrastructure && (
            <div className="dropdown-items">
              {infrastructureItems.map((item) => (
                <button
                  key={item.name}
                  className={`menu-btn sub-menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Security Dropdown */}
        <div className="menu-dropdown">
          <button
            className={`menu-btn dropdown-btn ${isDropdownActive(securityItems) ? 'active' : ''}`}
            onClick={() => toggleDropdown('security')}
          >
            <span className="menu-icon"><FiShield /></span>
            <span className="menu-text">Security</span>
            <span className="dropdown-arrow">
              {openDropdowns.security ? <FiChevronDown /> : <FiChevronRight />}
            </span>
          </button>
          {openDropdowns.security && (
            <div className="dropdown-items">
              {securityItems.map((item) => (
                <button
                  key={item.name}
                  className={`menu-btn sub-menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Systems Dropdown */}
        <div className="menu-dropdown">
          <button
            className={`menu-btn dropdown-btn ${isDropdownActive(systemsItems) ? 'active' : ''}`}
            onClick={() => toggleDropdown('systems')}
          >
            <span className="menu-icon"><FiCpu /></span>
            <span className="menu-text">Systems</span>
            <span className="dropdown-arrow">
              {openDropdowns.systems ? <FiChevronDown /> : <FiChevronRight />}
            </span>
          </button>
          {openDropdowns.systems && (
            <div className="dropdown-items">
              {systemsItems.map((item) => (
                <button
                  key={item.name}
                  className={`menu-btn sub-menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Network Dropdown */}
        <div className="menu-dropdown">
          <button
            className={`menu-btn dropdown-btn ${isDropdownActive(networkItems) ? 'active' : ''}`}
            onClick={() => toggleDropdown('network')}
          >
            <span className="menu-icon"><FiWifi /></span>
            <span className="menu-text">Network</span>
            <span className="dropdown-arrow">
              {openDropdowns.network ? <FiChevronDown /> : <FiChevronRight />}
            </span>
          </button>
          {openDropdowns.network && (
            <div className="dropdown-items">
              {networkItems.map((item) => (
                <button
                  key={item.name}
                  className={`menu-btn sub-menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ICT Support Dropdown */}
        <div className="menu-dropdown">
          <button
            className={`menu-btn dropdown-btn ${isDropdownActive(supportItems) ? 'active' : ''}`}
            onClick={() => toggleDropdown('support')}
          >
            <span className="menu-icon"><FiUsers /></span>
            <span className="menu-text">IT Support</span>
            <span className="dropdown-arrow">
              {openDropdowns.support ? <FiChevronDown /> : <FiChevronRight />}
            </span>
          </button>
          {openDropdowns.support && (
            <div className="dropdown-items">
              {supportItems.map((item) => (
                <button
                  key={item.name}
                  className={`menu-btn sub-menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                >
                  <span className="menu-icon">{item.icon}</span>
                  <span className="menu-text">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <hr className="menu-divider" />

        {/* Reports Section */}
        <div className="menu-section-label" style={{ marginTop: 4 }}>
          📊 Reports & Analytics
        </div>
        {reportItems.map((item) => (
          <button
            key={item.name}
            className={`menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
            onClick={() => handleMenuClick(item)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-text">{item.label}</span>
          </button>
        ))}

        <hr className="menu-divider" />

        {otherItems.map((item) => (
          <button
            key={item.name}
            className={`menu-btn ${activeMenuItem === item.name ? 'active' : ''}`}
            onClick={() => handleMenuClick(item)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-text">{item.label}</span>
          </button>
        ))}

        <hr className="menu-divider" />

        <div className="menu-section-label" style={{ marginTop: 4 }}>
          🛟 Support
        </div>
        <button className="menu-btn" style={{ opacity: 0.7 }}>
          <span className="menu-icon">
            <FiHelpCircle />
          </span>
          <span className="menu-text">IT Help</span>
        </button>
        <button className="menu-btn" style={{ opacity: 0.7 }}>
          <span className="menu-icon">
            <FiAlertCircle />
          </span>
          <span className="menu-text">System Status</span>
        </button>
      </div>

      <style>{`
        .menu-dropdown {
          width: 100%;
        }

        .dropdown-btn {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100% !important;
        }

        .dropdown-arrow {
          margin-left: auto;
          display: flex;
          align-items: center;
          font-size: 14px;
          color: #94a3b8;
          transition: transform 0.2s ease;
        }

        .dropdown-items {
          padding-left: 8px;
          margin: 2px 0;
          border-left: 2px solid rgba(30, 136, 229, 0.15);
          margin-left: 12px;
          animation: slideDown 0.3s ease;
        }

        .sub-menu-btn {
          padding-left: 20px !important;
          font-size: 0.85rem !important;
          opacity: 0.85;
        }

        .sub-menu-btn .menu-icon {
          font-size: 14px !important;
        }

        .sub-menu-btn.active {
          opacity: 1;
          background: rgba(30, 136, 229, 0.08);
          color: #1E88E5;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .menu-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 10px 14px;
          border: none;
          background: transparent;
          color: #475569;
          font-weight: 500;
          font-size: 0.9rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .menu-btn:hover {
          background: rgba(30, 136, 229, 0.05);
          color: #1E88E5;
        }

        .menu-btn.active {
          background: rgba(30, 136, 229, 0.08);
          color: #1E88E5;
          font-weight: 600;
        }

        .menu-btn.active .menu-icon {
          color: #1E88E5;
        }

        .menu-icon {
          display: flex;
          align-items: center;
          font-size: 18px;
          color: #94a3b8;
          min-width: 24px;
        }

        .menu-text {
          flex: 1;
        }

        .menu-section-label {
          font-size: 0.7rem;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 8px 14px 4px;
          margin-top: 8px;
        }

        .menu-divider {
          border: none;
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          margin: 8px 14px;
        }

        .ict-sidebar {
          width: 260px;
          background: #ffffff;
          border-right: 1px solid rgba(0, 0, 0, 0.06);
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .ict-brand {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        .brand-logo-full img {
          width: 100%;
          max-width: 160px;
          height: auto;
          display: block;
        }

        .menu-list {
          flex: 1;
          padding: 8px 12px;
          overflow-y: auto;
        }

        .ict-sidebar::-webkit-scrollbar {
          width: 4px;
        }

        .ict-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }

        .ict-sidebar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .ict-sidebar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </aside>
  );
};

export default Sidebar;